import React, { useRef, useEffect, useState, useCallback, ReactNode } from 'react'
import Icon from '@/components/icon'
import Styles from './style.module.scss'
import { Flex } from 'antd'

interface HorizontalScrollProps {
  itemsPerScreen: number
  gap: number
  children: ReactNode
}

const SCROLL_TYPE_THRESHOLDS = {
  SHORT_PRESS: 300, // 毫秒
  SHORT_DISTANCE: 0.3, // 屏幕宽度的30%
  LONG_DISTANCE: 1.5, // 屏幕宽度的150%
}

const HorizontalScroll: React.FC<HorizontalScrollProps> = ({ itemsPerScreen, gap, children }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({
    itemWidth: 0,
    screenWidth: 0,
    maxScroll: 0,
  })

  // 新增滚动位置状态
  const [scrollPosition, setScrollPosition] = useState(0)
  // 新增箭头禁用状态
  const [arrowsDisabled, setArrowsDisabled] = useState({
    left: true,
    right: false,
  })

  // 触摸跟踪数据
  const touchData = useRef({
    startX: 0,
    startTime: 0,
    lastX: 0,
    isScrolling: false,
  })

  // 计算基础参数
  const updateDimensions = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const screenWidth = container.clientWidth
    const itemWidth = (screenWidth - gap * (itemsPerScreen - 1)) / itemsPerScreen

    setDimensions({
      itemWidth,
      screenWidth,
      maxScroll: container.scrollWidth - screenWidth,
    })
  }, [gap, itemsPerScreen])

  // 核心滚动逻辑
  const scrollToPosition = useCallback(
    (target: number) => {
      const container = containerRef.current
      if (!container) return

      // 边界约束
      target = Math.max(0, Math.min(target, dimensions.maxScroll))

      container.scrollTo({
        left: target,
        behavior: 'smooth',
      })
    },
    [dimensions.maxScroll]
  )

  // 触摸事件处理
  const handleTouchStart = (e: React.TouchEvent) => {
    touchData.current = {
      startX: e.touches[0].clientX,
      startTime: Date.now(),
      lastX: e.touches[0].clientX,
      isScrolling: true,
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchData.current.isScrolling) return
    touchData.current.lastX = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    const { startX, lastX, startTime } = touchData.current
    const deltaX = startX - lastX
    const duration = Date.now() - startTime

    // 计算滚动类型
    const screenWidth = dimensions.screenWidth
    const isShortPress = duration < SCROLL_TYPE_THRESHOLDS.SHORT_PRESS
    const moveRatio = Math.abs(deltaX) / screenWidth

    let targetScroll = 0
    const currentScroll = containerRef.current?.scrollLeft || 0

    if (isShortPress) {
      // 短触滚动逻辑
      if (moveRatio < SCROLL_TYPE_THRESHOLDS.SHORT_DISTANCE) {
        // 短距离：移动一个项目
        targetScroll = currentScroll + (deltaX > 0 ? 1 : -1) * (dimensions.itemWidth + gap)
      } else if (moveRatio < 1) {
        // 中距离：移动一屏
        targetScroll = currentScroll + (deltaX > 0 ? 1 : -1) * screenWidth
      } else {
        // 长距离：移动两屏
        targetScroll = currentScroll + (deltaX > 0 ? 2 : -2) * screenWidth
      }
    } else {
      // 长触按原有逻辑计算
      const visibleThreshold = 0.1 * screenWidth
      const itemStep = dimensions.itemWidth + gap
      const rawIndex = (currentScroll + visibleThreshold) / itemStep
      targetScroll = Math.round(rawIndex) * itemStep - visibleThreshold
    }

    scrollToPosition(targetScroll)
    touchData.current.isScrolling = false
  }

  // 更新箭头状态
  const updateArrowsState = useCallback(
    (currentScroll: number) => {
      const newState = {
        left: currentScroll <= 0,
        right: currentScroll >= dimensions.maxScroll - 1, // 防止小数误差
      }
      setArrowsDisabled(newState)
    },
    [dimensions.maxScroll]
  )

  // 修改后的滚动处理
  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const currentScroll = container.scrollLeft
    setScrollPosition(currentScroll)
    updateArrowsState(currentScroll)

    // 原有边界处理逻辑
    if (currentScroll <= 0 || currentScroll >= dimensions.maxScroll) {
      container.style.overflowX = 'hidden'
      setTimeout(() => {
        if (container) container.style.overflowX = 'auto'
      }, 100)
    }
  }, [dimensions.maxScroll, updateArrowsState])

  // 新增箭头点击处理
  const handleArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      const container = containerRef.current
      if (!container || !dimensions.screenWidth) return

      const scrollAmount = dimensions.screenWidth
      const target = direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount

      scrollToPosition(target)
    },
    [dimensions.screenWidth, scrollToPosition]
  )

  // 初始化
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    updateDimensions()
    const observer = new ResizeObserver(updateDimensions)
    observer.observe(container)
    return () => observer.disconnect()
  }, [updateDimensions])

  // 箭头组件
  const ArrowButton = ({ direction }: { direction: 'left' | 'right' }) =>
    direction === 'left' ? (
      <Icon
        name='arrowBlack'
        className={`${Styles.arrow} ${Styles.prev}`}
        onClick={() => handleArrowClick(direction)}
        style={{
          opacity: arrowsDisabled[direction] ? 0.3 : 1,
          pointerEvents: arrowsDisabled[direction] ? 'none' : 'auto',
        }}
      />
    ) : (
      <Icon
        name='arrowBlack'
        className={`${Styles.arrow} ${Styles.next}`}
        onClick={() => handleArrowClick(direction)}
        style={{
          opacity: arrowsDisabled[direction] ? 0.3 : 1,
          pointerEvents: arrowsDisabled[direction] ? 'none' : 'auto',
        }}
      />
    )
  // <button
  //   onClick={() => handleArrowClick(direction)}
  //   disabled={direction === 'left' ? arrowsDisabled.left : arrowsDisabled.right}
  //   style={{
  //     transform: 'translateY(-50%)',
  //     opacity: arrowsDisabled[direction] ? 0.3 : 1,
  //     pointerEvents: arrowsDisabled[direction] ? 'none' : 'auto',
  //     background: 'rgba(255, 255, 255, 0.8)',
  //     borderRadius: '50%',
  //     width: '40px',
  //     height: '40px',
  //     border: 'none',
  //     boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  //     cursor: 'pointer',
  //     transition: 'opacity 0.2s',
  //   }}
  //   aria-label={`Scroll ${direction}`}
  // >
  //   {direction === 'left' ? '←' : '→'}
  // </button>

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onScroll={handleScroll}
        style={{
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: `${gap}px`,
            width: 'fit-content',
            // width: `calc(${100 * React.Children.count(children)}% + ${gap * (React.Children.count(children) - 1)}px)`,
          }}
        >
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              style={{
                flex: `0 0 calc(${100 / itemsPerScreen}vw - ${(gap * (itemsPerScreen - 1)) / itemsPerScreen}px)`,
                scrollSnapAlign: 'start',
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      <Flex justify='flex-end' className={Styles.footer}>
        <ArrowButton direction='left' />
        <ArrowButton direction='right' />
      </Flex>
    </div>
  )
}

export default HorizontalScroll
